import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { getNewVisit } from '../../lib/services/formService';
import {
  formatAgeFirstAttack,
  formatAuraSymptoms,
  formatFrequencyDuration,
  formatHeadacheDaysPerMonth,
  formatHeadacheDuration,
  formatHeadacheFrequency,
  formatPainCharacter,
  formatPainIntensity,
  formatPainLocation,
  formatPainMedicationEffect,
  formatPainMedicationEffectiveness,
  formatPainMedicationFrequency,
  formatBreastfeeding,
  formatPregnancyWeek,
  formatPreferredActionDuringAttack,
  formatTriggersMessage,
} from '../../lib/helpers/formFormatters';
import SectionCard from './SectionCard';
import JournalHeader from './JournalHeader';
import { DS } from './designTokens';
import { formatFormStatus } from './formUtils';
import { reportError } from '../../lib/sentry';
import JournalCopyPanel from './JournalCopyPanel';
import {
  AdditionalHeadache,
  Allergy,
  Medication,
  OtherMedication,
  PreventiveMedication,
  PreviousMedication,
  PreviousPreventiveMedication,
} from '../PatientFormStandalone/types';

const hasValue = (val: unknown) => typeof val === 'string' && val.trim() !== '';
const hasAny = (...vals: unknown[]) => vals.some(hasValue);

function formatAttackPatternDuring(attackPatternDuring: string): string {
  if (attackPatternDuring === 'yes') {
    return 'under perioder av veckor-månader, sedan besvärsfrihet under månader-år innan ny period börjar.';
  }
  if (attackPatternDuring === 'no') {
    return 'kontinuerligt sedan minst ett år tillbaka, möjligen med uppehåll under mindre än tre månader.';
  }
  return '';
}

interface NewVisitJournalProps {
  patient: { _id: string };
  onBack: () => void;
}

const NewVisitJournal: React.FC<NewVisitJournalProps> = ({
  patient,
  onBack,
}) => {
  const [loading, setLoading] = useState(true);
  const [formStatus, setFormStatus] = useState('Ej påbörjad');
  const [status, setStatus] = useState('pending');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- UserAnswers declares only some of the questionnaire fields rendered here; typing the rest is a types refactor
  const [formData, setFormData] = useState<any>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getNewVisit(patient._id);
        const { form } = res.data.result;
        if (form) {
          setFormStatus(formatFormStatus(form.status, form.submittedAt));
          setStatus(form.status);
          setFormData(form.formQuestionnaire?.data);
        }
      } catch (err) {
        reportError(err, { tags: { area: 'new_visit_journal' } });
        setFormStatus('Fel vid hämtning');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [patient._id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: DS.deepTeal }} />
      </Box>
    );
  }

  const migran =
    hasValue(formData?.diagnosisConditionsData) &&
    hasValue(formData?.diagnosedConditionsDateMigran)
      ? `Patienten uppger sig sedan tidigare diagnosticerad med migrän. Diagnos ställd av ${formData.diagnosisConditionsData.toLowerCase()} år ${formData.diagnosedConditionsDateMigran}.`
      : '';
  const spanningshuvudvark =
    hasValue(formData?.diagnosisConditionsDataSpanningshuvudvark) &&
    hasValue(formData?.diagnosedConditionsDateSpanningshuvudvark)
      ? `Patienten uppger sig sedan tidigare diagnosticerad med spänningshuvudvärk. Diagnos ställd av ${formData.diagnosisConditionsDataSpanningshuvudvark.toLowerCase()} år ${formData.diagnosedConditionsDateSpanningshuvudvark}.`
      : '';
  const horton =
    hasValue(formData?.diagnosisConditionsDataHorton) &&
    hasValue(formData?.diagnosedConditionsDateHorton)
      ? `Patienten uppger sig sedan tidigare diagnosticerad med horton. Diagnos ställd av ${formData.diagnosisConditionsDataHorton.toLowerCase()} år ${formData.diagnosedConditionsDateHorton}.`
      : '';
  const otherHeadache =
    hasValue(formData?.otherHeadacheType) &&
    hasValue(formData?.diagnosedConditionsDateOther)
      ? `Patienten uppger sig sedan tidigare diagnosticerad med annan huvudvärk. Typ samt år för diagnos: ${formData.otherHeadacheType.toLowerCase()} år ${formData.diagnosedConditionsDateOther}.`
      : '';
  const hasDiagnosedHeadache = hasAny(
    migran,
    spanningshuvudvark,
    horton,
    otherHeadache
  );
  const noDiagnose =
    !hasDiagnosedHeadache && formData?.diagnosedConditions?.includes('ej vara')
      ? 'Patienten uppger sig sedan tidigare ej vara diagnosticerad med huvudvärkssjukdom.'
      : '';
  const shouldShowAnamnes = hasAny(
    migran,
    spanningshuvudvark,
    horton,
    otherHeadache,
    noDiagnose
  );

  return (
    <Box sx={{ fontFamily: DS.fontBody, maxWidth: 720 }}>
      <JournalHeader
        title="Nybesök – Patientsvar"
        status={status}
        formStatus={formStatus}
        onBack={onBack}
      />

      {formData && (
        <JournalCopyPanel>
          {hasValue(formData.headacheLogged) && (
            <SectionCard title="Dagbok">
              <span>{formData.headacheLogged?.trim()}</span>
            </SectionCard>
          )}

          {shouldShowAnamnes && (
            <SectionCard title="Anamnes">
              {hasValue(migran) && <p>{migran}</p>}
              {hasValue(spanningshuvudvark) && <p>{spanningshuvudvark}</p>}
              {hasValue(horton) && (
                <p>{horton.replace('horton', 'klusterhuvudvärk')}</p>
              )}
              {hasValue(otherHeadache) && <p>{otherHeadache}</p>}
              {hasValue(noDiagnose) && <p>{noDiagnose}</p>}
            </SectionCard>
          )}

          {hasValue(formData.hasMultipleHeadaches) && (
            <SectionCard title="Aktuellt">
              <p>{formData.hasMultipleHeadaches}</p>
            </SectionCard>
          )}

          {hasAny(
            formData.ageFirstAttack,
            formData.headacheDuration,
            formData.painLocation,
            formData.attackPattern,
            formData.attackPatternDuring,
            formData.painCharacter,
            formData.painIntensity,
            formData.physicalActivityEffect,
            formData.preferredActionDuringAttack,
            formData.associatedSymptoms,
            formData.auraSymptoms,
            formData.dizzinessType,
            formData.dizzinessDuration,
            formData.headacheFrequency,
            formData.frequencyDuration,
            formData.initialFrequency,
            formData.worseningOverTime,
            formData.worseningDuration,
            formData.triggers
          ) && (
            <SectionCard title="Huvudvärk 1">
              {hasValue(formData.ageFirstAttack) && (
                <p>{formData.ageFirstAttack}</p>
              )}
              {hasValue(formData.headacheDuration) && (
                <p>{formData.headacheDuration}</p>
              )}
              {hasValue(formData.painLocation) && (
                <p>{formData.painLocation}</p>
              )}
              {hasAny(formData.attackPattern, formData.attackPatternDuring) &&
                hasValue(formData.attackPattern) && (
                  <p>
                    {formData.attackPattern}{' '}
                    {hasValue(formData.attackPatternDuring)
                      ? formData.attackPatternDuring
                      : ''}
                  </p>
                )}
              {hasValue(formData.painCharacter) && (
                <p>{formData.painCharacter}</p>
              )}
              {hasValue(formData.painIntensity) && (
                <p>{formData.painIntensity}</p>
              )}
              {hasValue(formData.physicalActivityEffect) && (
                <p>{formData.physicalActivityEffect}</p>
              )}
              {hasValue(formData.preferredActionDuringAttack) && (
                <p>{formData.preferredActionDuringAttack}</p>
              )}
              {hasValue(formData.associatedSymptoms) && (
                <p>{formData.associatedSymptoms}</p>
              )}
              {hasValue(formData.auraSymptoms) && (
                <p>{formData.auraSymptoms}</p>
              )}
              {hasValue(formData.dizzinessType) && (
                <p>{formData.dizzinessType}</p>
              )}
              {hasValue(formData.dizzinessDuration) && (
                <p>{formData.dizzinessDuration}</p>
              )}
              {hasValue(formData.headacheFrequency) && (
                <p>
                  {formData.headacheFrequency}{' '}
                  {hasValue(formData.frequencyDuration)
                    ? formData.frequencyDuration
                    : ''}
                </p>
              )}
              {hasValue(formData.initialFrequency) && (
                <p>{formData.initialFrequency}</p>
              )}
              {hasValue(formData.worseningOverTime) && (
                <p>{formData.worseningOverTime}</p>
              )}
              {hasValue(formData.worseningDuration) && (
                <p>{formData.worseningDuration}</p>
              )}
              {hasValue(formData.triggers) && <p>{formData.triggers}</p>}
            </SectionCard>
          )}

          {formData.additionalHeadaches?.length > 0 &&
            formData.additionalHeadaches.map(
              (headache: AdditionalHeadache, index: number) => {
                const shouldRender =
                  hasAny(
                    headache.ageFirstAttackHeadache,
                    headache.headacheDurationHeadache,
                    headache.painLocationHeadache,
                    headache.preferredActionDuringAttackHeadache,
                    headache.dizzinessTypeHeadache,
                    headache.dizzinessDurationHeadache,
                    headache.headacheFrequencyHeadache,
                    headache.initialFrequencyHeadache,
                    headache.frequencyDurationHeadache,
                    headache.worseningDurationHeadache
                  ) ||
                  Array.isArray(headache.associatedSymptomHeadaches) ||
                  Array.isArray(headache.auraSymptomsHeadache) ||
                  Array.isArray(headache.triggersHeadache) ||
                  headache.attackPatternHeadache === 'yes' ||
                  headache.physicalActivityEffectHeadache === 'yes' ||
                  headache.worseningOverTimeHeadache === 'yes';
                if (!shouldRender) return null;
                return (
                  // eslint-disable-next-line react/no-array-index-key -- saved answers have no id and this read-only view never reorders them
                  <SectionCard key={index} title={`Huvudvärk ${index + 2}`}>
                    {hasValue(headache.ageFirstAttackHeadache) && (
                      <p>
                        {formatAgeFirstAttack(headache.ageFirstAttackHeadache)}
                      </p>
                    )}
                    {hasValue(headache.headacheDurationHeadache) && (
                      <p>
                        {formatHeadacheDuration(
                          headache.headacheDurationHeadache
                        )}
                      </p>
                    )}
                    {hasValue(headache.painLocationHeadache) && (
                      <p>{formatPainLocation(headache.painLocationHeadache)}</p>
                    )}
                    <p>
                      {headache.attackPatternHeadache === 'yes'
                        ? 'Attacker minst varannan dag upp till 8 ggr dagligen'
                        : 'Inget clustermönster'}
                      {`, ${formatAttackPatternDuring(
                        headache.attackPatternDuringHeadache
                      )}`}
                    </p>
                    {hasValue(headache.painCharacterHeadache) && (
                      <p>
                        {formatPainCharacter(headache.painCharacterHeadache)}
                      </p>
                    )}
                    {hasValue(headache.painIntensityHeadache) && (
                      <p>
                        {formatPainIntensity(headache.painIntensityHeadache)}
                      </p>
                    )}
                    <p>
                      {headache.physicalActivityEffectHeadache === 'yes'
                        ? 'Pågående huvudvärk förvärras av normal fysisk aktivitet.'
                        : 'Pågående huvudvärk förvärras inte av normal fysisk aktivitet.'}
                    </p>
                    {hasValue(headache.preferredActionDuringAttackHeadache) && (
                      <p>
                        {formatPreferredActionDuringAttack(
                          headache.preferredActionDuringAttackHeadache
                        )}
                      </p>
                    )}
                    {Array.isArray(headache.associatedSymptomHeadaches) &&
                      headache.associatedSymptomHeadaches.length > 0 && (
                        <p>
                          {headache.associatedSymptomHeadaches.includes(
                            'Nej, inget av ovanstående'
                          )
                            ? 'Förnekar associerade symtom.'
                            : `Associerade symptom i form av:  ${headache.associatedSymptomHeadaches.join(', ')}`}
                        </p>
                      )}
                    {Array.isArray(headache.auraSymptomsHeadache) && (
                      <p>{formatAuraSymptoms(headache.auraSymptomsHeadache)}</p>
                    )}
                    {hasValue(headache.dizzinessTypeHeadache) && (
                      <p>{`Yrsel beskrivs som ${headache.dizzinessTypeHeadache}`}</p>
                    )}
                    {hasValue(headache.dizzinessDurationHeadache) && (
                      <p>{`Uppger ${headache.dizzinessDurationHeadache} duration av nämnda symtom.`}</p>
                    )}
                    {hasValue(headache.headacheFrequencyHeadache) && (
                      <p>
                        {formatHeadacheFrequency(
                          headache.headacheFrequencyHeadache
                        ) +
                          formatFrequencyDuration(
                            headache.frequencyDurationHeadache
                          )}
                      </p>
                    )}
                    {hasValue(headache.initialFrequencyHeadache) && (
                      <p>{`De första åren efter debuten var huvudvärksfrekvensen ${headache.initialFrequencyHeadache}`}</p>
                    )}
                    <p>
                      {headache.worseningOverTimeHeadache === 'yes'
                        ? 'Anfallsfrekvensen har försämrats med tiden.'
                        : 'Anfallsfrekvensen har varit oförändrad sedan debuten.'}
                    </p>
                    {hasValue(headache.worseningDurationHeadache) && (
                      <p>{`Anfallsfrekvensen har ökat sedan ${headache.worseningDurationHeadache}.`}</p>
                    )}
                    {Array.isArray(headache.triggersHeadache) && (
                      <p>{formatTriggersMessage(headache.triggersHeadache)}</p>
                    )}
                  </SectionCard>
                );
              }
            )}

          {hasValue(formData.headacheFreeDays) &&
            typeof formData.headacheDaysPerMonth !== 'number' && (
              <SectionCard title="Slutsummat antal huvudvärksfria dagar per månad">
                <p>{formData.headacheFreeDays}</p>
              </SectionCard>
            )}

          {typeof formData.headacheDaysPerMonth === 'number' && (
            <SectionCard title="Uppskattat antal huvudvärksdagar per månad">
              <p>{formatHeadacheDaysPerMonth(formData.headacheDaysPerMonth)}</p>
            </SectionCard>
          )}

          {hasValue(formData.comorbidities) && (
            <SectionCard title="Predisponderande faktorer/komorbiditet">
              <p>{formData.comorbidities}</p>
            </SectionCard>
          )}

          {hasValue(formData.nonMedicationTreatments) &&
            !hasAny(
              formData.massageEffect,
              formData.naprapatEffect,
              formData.acupunctureEffect,
              formData.tensEffect,
              formData.yogaEffect,
              formData.bettskenEffect
            ) && (
              <SectionCard title="Ickefarmakologisk behandling">
                <p>{formData.nonMedicationTreatments}</p>
              </SectionCard>
            )}

          {hasAny(
            formData.massageEffect,
            formData.naprapatEffect,
            formData.acupunctureEffect,
            formData.tensEffect,
            formData.yogaEffect,
            formData.bettskenEffect
          ) && (
            <SectionCard title="Ickefarmakologisk behandling">
              <p>Har av icke medikamentella behandlingar provat:</p>
              {hasValue(formData.massageEffect) && (
                <p>
                  Massage, med {formData.massageEffect.toLowerCase()} effekt.
                </p>
              )}
              {hasValue(formData.naprapatEffect) && (
                <p>
                  Naprapat / kiropraktor behandling, med{' '}
                  {formData.naprapatEffect.toLowerCase()} effekt.
                </p>
              )}
              {hasValue(formData.acupunctureEffect) && (
                <p>
                  Akupunktur, med {formData.acupunctureEffect.toLowerCase()}{' '}
                  effekt.
                </p>
              )}
              {hasValue(formData.tensEffect) && (
                <p>TENS, med {formData.tensEffect.toLowerCase()} effekt</p>
              )}
              {hasValue(formData.yogaEffect) && (
                <p>
                  Yoga eller annan avslappningsmetod, med{' '}
                  {formData.yogaEffect.toLowerCase()} effekt.
                </p>
              )}
              {hasValue(formData.bettskenEffect) && (
                <p>
                  Bettskena, med {formData.bettskenEffect.toLowerCase()} effekt.
                </p>
              )}
            </SectionCard>
          )}

          {hasAny(
            formData.medicationOveruse,
            formData.overusedMedication,
            formData.overuseDuration,
            formData.hasWithdrawal,
            formData.withdrawalDate,
            formData.withdrawalEffect
          ) && (
            <SectionCard title="Riskbedömning överkonsumtion">
              {hasAny(
                formData.medicationOveruse,
                formData.overusedMedication
              ) && (
                <p>{`${[formData.medicationOveruse, formData.overusedMedication].filter(hasValue).join(' ').replace(/\.*$/, '')}.`}</p>
              )}
              {hasValue(formData.overuseDuration) && (
                <p>{formData.overuseDuration}</p>
              )}
              {hasAny(
                formData.hasWithdrawal,
                formData.withdrawalDate,
                formData.withdrawalEffect
              ) && (
                <p>
                  {formData.hasWithdrawal ?? ''}
                  {formData.withdrawalDate ? ` ${formData.withdrawalDate}` : ''}
                  {formData.withdrawalEffect
                    ? ` ${formData.withdrawalEffect}`
                    : ''}
                </p>
              )}
            </SectionCard>
          )}

          {hasAny(
            formData.radiologyExam,
            formData.radiologyDate,
            formData.radiologyLocation,
            formData.radiologyResult,
            formData.describeAnomaly,
            formData.controllMrt,
            formData.mrtDate,
            formData.mrtLocation,
            formData.mrtResult,
            formData.additionalRadiologyInfo
          ) && (
            <SectionCard title="Radiologi">
              {hasAny(
                formData.radiologyExam,
                formData.radiologyDate,
                formData.radiologyLocation
              ) && (
                <p>
                  {formData.radiologyExam ?? ''}{' '}
                  {formData.radiologyDate ? `${formData.radiologyDate}, ` : ''}
                  {formData.radiologyLocation
                    ? `${formData.radiologyLocation}. `
                    : ''}
                  {hasValue(formData.radiologyResult) &&
                    ` ${formData.radiologyResult}`}
                  {hasValue(formData.describeAnomaly) &&
                    `: ${formData.describeAnomaly}`}
                </p>
              )}
              {hasAny(
                formData.controllMrt,
                formData.mrtDate,
                formData.mrtLocation
              ) && (
                <p>
                  {formData.controllMrt ?? ''}{' '}
                  {formData.mrtDate ? `${formData.mrtDate}, ` : ''}
                  {formData.mrtLocation ? `${formData.mrtLocation}. ` : ''}
                  {hasValue(formData.mrtResult) && ` ${formData.mrtResult}`}
                  {hasValue(formData.additionalRadiologyInfo) &&
                    `: ${formData.additionalRadiologyInfo}`}
                </p>
              )}
            </SectionCard>
          )}

          {hasAny(
            formData.takesPainMedication,
            formData.painMedicationName,
            formData.painMedicationDose,
            formData.painMedicationEffect,
            formData.painMedicationEffectiveness,
            formData.painMedicationFrequency
          ) && (
            <SectionCard title="Anfallsbehandling">
              {hasValue(formData.takesPainMedication) && (
                <p>{formData.takesPainMedication}</p>
              )}
              {hasAny(
                formData.painMedicationName,
                formData.painMedicationDose,
                formData.painMedicationEffect
              ) && (
                <p>
                  {formData.painMedicationName ?? ''}
                  {formData.painMedicationDose
                    ? ` ${formData.painMedicationDose}`
                    : ''}
                  {formData.painMedicationEffect
                    ? ` ${formData.painMedicationEffect}`
                    : ''}
                </p>
              )}
              {hasAny(formData.painMedicationEffectiveness) && (
                <p>
                  {formData.painMedicationEffectiveness
                    ? ` ${formData.painMedicationEffectiveness}`
                    : ''}
                </p>
              )}
              {hasAny(formData.painMedicationFrequency) && (
                <p>
                  {formData.painMedicationFrequency
                    ? ` ${formData.painMedicationFrequency}`
                    : ''}
                </p>
              )}
              {Array.isArray(formData.additionalAttackMedications) &&
                formData.additionalAttackMedications.map(
                  (medication: Medication, index: number) => {
                    const { name, dose, effect, frequency, effectiveness } =
                      medication;
                    if (!hasAny(name, dose, effect, frequency, effectiveness))
                      return null;
                    return (
                      // eslint-disable-next-line react/no-array-index-key -- saved answers have no id and this read-only view never reorders them
                      <Box key={index} sx={{ mt: 1 }}>
                        <p>Vid anfall tas även ({index + 2})</p>
                        {(name || dose || effect) && (
                          <p>
                            {name ?? ''}
                            {dose ? ` ${dose}` : ''}
                            {effect
                              ? ` ${formatPainMedicationEffect(effect)}`
                              : ''}
                          </p>
                        )}
                        {effectiveness && (
                          <p>
                            {formatPainMedicationEffectiveness(effectiveness)}
                          </p>
                        )}
                        {frequency && (
                          <p>{formatPainMedicationFrequency(frequency)}</p>
                        )}
                      </Box>
                    );
                  }
                )}
              {hasAny(
                formData.previousMedicationName,
                formData.previousMedicationDose,
                formData.previousMedicationFrequency,
                formData.previousMedicationStartYear,
                formData.previousMedicationStopReason,
                formData.previousMedicationOtherReason,
                formData.previousMedicationSideEffect,
                formData.previousMedicationStopYear
              ) && (
                <Box sx={{ mt: 1 }}>
                  <p>Har tidigare provat (1) </p>
                  {hasAny(
                    formData.previousMedicationName,
                    formData.previousMedicationDose
                  ) && (
                    <p>
                      {formData.previousMedicationName
                        ? `${formData.previousMedicationName}`
                        : ''}
                      {formData.previousMedicationDose
                        ? ` ${formData.previousMedicationDose}`
                        : ''}
                    </p>
                  )}
                  {formData.previousMedicationFrequency &&
                    formData.previousMedicationStartYear && (
                      <p>
                        Tog denna {formData.previousMedicationFrequency} med
                        start {formData.previousMedicationStartYear}
                      </p>
                    )}
                  {formData.previousMedicationStopReason &&
                    formData.previousMedicationStopYear && (
                      <p>
                        Utsattes pga{' '}
                        {formData.previousMedicationStopReason.toLowerCase()}
                        {formData.previousMedicationOtherReason
                          ? `: ${formData.previousMedicationOtherReason}`
                          : ''}
                        {formData.previousMedicationSideEffect
                          ? `: ${formData.previousMedicationSideEffect}`
                          : ''}
                        , år {formData.previousMedicationStopYear}.
                      </p>
                    )}
                </Box>
              )}
              {Array.isArray(formData.additionalPreviousMedications) &&
                formData.additionalPreviousMedications.map(
                  (medication: PreviousMedication, index: number) => {
                    const {
                      name,
                      dose,
                      frequency,
                      startYear,
                      stopReason,
                      sideEffects,
                      stopReasonDetails,
                      stopYear,
                    } = medication;
                    if (
                      !hasAny(
                        name,
                        dose,
                        frequency,
                        startYear,
                        stopReason,
                        sideEffects,
                        stopReasonDetails,
                        stopYear
                      )
                    )
                      return null;
                    return (
                      // eslint-disable-next-line react/no-array-index-key -- saved answers have no id and this read-only view never reorders them
                      <Box key={index} sx={{ mt: 1 }}>
                        <p>Har även provat ({index + 2})</p>
                        {(name || dose) && (
                          <p>
                            {name ?? ''}
                            {dose ? ` ${dose}` : ''}
                          </p>
                        )}
                        {(frequency || startYear) && (
                          <p>
                            Tog denna {frequency} med start {startYear}
                          </p>
                        )}
                        {(stopReason || stopYear) && (
                          <p>
                            Utsattes pga {stopReason.toLowerCase()}
                            {stopReasonDetails ? `: ${stopReasonDetails}` : ''}
                            {sideEffects ? `: ${sideEffects}` : ''} år{' '}
                            {stopYear}.
                          </p>
                        )}
                      </Box>
                    );
                  }
                )}
            </SectionCard>
          )}

          {hasAny(
            formData.preventiveMedicationHeadic,
            formData.preventiveMedicationNameHeadic,
            formData.preventiveMedicationDoseHeadic,
            formData.preventiveMedicationEffectivenessHeadic,
            formData.preventiveMedicationStartYearHeadic,
            formData.preventiveMedicationSideEffectDetailsHeadic
          ) && (
            <SectionCard title="Profylax">
              {hasValue(formData.preventiveMedicationHeadic) && (
                <p>{formData.preventiveMedicationHeadic}</p>
              )}
              {hasAny(
                formData.preventiveMedicationNameHeadic,
                formData.preventiveMedicationDoseHeadic
              ) && (
                <p>
                  {formData.preventiveMedicationNameHeadic ?? ''}
                  {formData.preventiveMedicationDoseHeadic
                    ? ` ${formData.preventiveMedicationDoseHeadic}`
                    : ''}
                </p>
              )}
              {hasValue(formData.preventiveMedicationEffectivenessHeadic) && (
                <p>{`Upplever ${formData.preventiveMedicationEffectivenessHeadic.toLowerCase()}.`}</p>
              )}
              {hasValue(formData.preventiveMedicationStartYearHeadic) && (
                <p>{`Började med denna ${formData.preventiveMedicationStartYearHeadic}.`}</p>
              )}
              {hasValue(
                formData.preventiveMedicationSideEffectDetailsHeadic
              ) && (
                <p>
                  Biverkan i form av{' '}
                  {formData.preventiveMedicationSideEffectDetailsHeadic}
                </p>
              )}
              {Array.isArray(formData.additionalPreventiveMedications) &&
                formData.additionalPreventiveMedications.map(
                  (med: PreventiveMedication, index: number) => {
                    const { name, dose, effect, startYear, sideEffectDetails } =
                      med;
                    if (
                      !hasAny(name, dose, effect, startYear, sideEffectDetails)
                    )
                      return null;
                    return (
                      // eslint-disable-next-line react/no-array-index-key -- saved answers have no id and this read-only view never reorders them
                      <Box key={index} sx={{ mt: 1 }}>
                        <p>Behandlas även med ({index + 2})</p>
                        <p>
                          {name} {dose}
                        </p>
                        <p>Upplever {effect?.toLocaleLowerCase()}.</p>
                        {startYear && <p>Började med denna {startYear}</p>}
                        {sideEffectDetails && (
                          <p>Biverkan i form av {sideEffectDetails}</p>
                        )}
                      </Box>
                    );
                  }
                )}
              {Array.isArray(formData.previousPreventiveMedications) &&
                formData.previousPreventiveMedications.map(
                  (medication: PreviousPreventiveMedication, index: number) => {
                    const {
                      name,
                      dose,
                      startYear,
                      stopReason,
                      sideEffects,
                      stopReasonDetails,
                      stopYear,
                    } = medication;
                    if (
                      !hasAny(
                        name,
                        dose,
                        startYear,
                        stopReason,
                        sideEffects,
                        stopReasonDetails,
                        stopYear
                      )
                    )
                      return null;
                    return (
                      // eslint-disable-next-line react/no-array-index-key -- saved answers have no id and this read-only view never reorders them
                      <Box key={index} sx={{ mt: 1 }}>
                        <p>{`${index > 0 ? 'Även provat' : 'Har tidigare behandlats med'} (${index + 1})`}</p>
                        {(name || dose) && (
                          <p>
                            {name ?? ''}
                            {dose ? ` ${dose}` : ''}
                          </p>
                        )}
                        {startYear && <p>Började med denna {startYear}</p>}
                        {(stopReason || stopYear) && (
                          <p>
                            Utsattes pga {stopReason.toLowerCase()}
                            {stopReasonDetails ? `: ${stopReasonDetails}` : ''}
                            {sideEffects ? `: ${sideEffects}` : ''} år{' '}
                            {stopYear}.
                          </p>
                        )}
                      </Box>
                    );
                  }
                )}
            </SectionCard>
          )}

          {Array.isArray(formData.otherMedications) &&
            formData.otherMedications.length > 0 && (
              <SectionCard title="Övriga läkemedel">
                {formData.otherMedications.map(
                  (med: OtherMedication, index: number) => {
                    if (!med.name?.trim()) return null;
                    return (
                      // eslint-disable-next-line react/no-array-index-key -- saved answers have no id and this read-only view never reorders them
                      <p key={index}>
                        {`Övrig medicin (${index + 1})`}: {med.name}
                      </p>
                    );
                  }
                )}
              </SectionCard>
            )}

          {formData.otherPreventiveMedicationOtherNo === 'Nej' && (
            <SectionCard title="Allergi">
              <p>Nekar läkemedelsallergi</p>
            </SectionCard>
          )}

          {formData.otherPreventiveMedicationOtherNo === 'Ja' &&
            Array.isArray(formData.allergies) &&
            formData.allergies.filter((a: Allergy) =>
              hasAny(a.allergen, a.reaction)
            ).length > 0 && (
              <SectionCard title="Allergi">
                <p>
                  Uppger följande läkemedelsallergi:{' '}
                  {formData.allergies[0].allergen}{' '}
                  {formData.allergies[0].reaction}
                </p>
                {formData.allergies.length > 1 && (
                  <>
                    <p>Övrig allergi:</p>
                    {formData.allergies
                      .slice(1)
                      .map((a: Allergy, i: number) => (
                        // eslint-disable-next-line react/no-array-index-key -- saved answers have no id and this read-only view never reorders them
                        <p key={i}>
                          {a.allergen} {a.reaction}
                        </p>
                      ))}
                  </>
                )}
              </SectionCard>
            )}

          {hasAny(
            formData.heredityMigraine,
            formData.neurologicalFamilyDiseases,
            formData.neurologicalFamilyDiagnosis,
            formData.neurologicalFamilyRelation
          ) && (
            <SectionCard title="Hereditet">
              {hasValue(formData.heredityMigraine) && (
                <p>{formData.heredityMigraine}</p>
              )}
              {hasValue(formData.neurologicalFamilyDiseases) && (
                <p>{formData.neurologicalFamilyDiseases}</p>
              )}
              {hasAny(
                formData.neurologicalFamilyDiagnosis,
                formData.neurologicalFamilyRelation
              ) && (
                <p>
                  {formData.neurologicalFamilyDiagnosis ?? ''}
                  {formData.neurologicalFamilyRelation
                    ? ` (${formData.neurologicalFamilyRelation})`
                    : ''}
                </p>
              )}
            </SectionCard>
          )}

          {hasAny(
            formData.otherConditions,
            formData.otherConditionDetails,
            formData.hypertension,
            formData.bloodPressureLastYear === 'yes' &&
              formData.bloodPressureValue
          ) && (
            <SectionCard title="Tidigare sjukdomar">
              {hasValue(formData.otherConditions) && (
                <p>{formData.otherConditions}</p>
              )}
              {hasValue(formData.otherConditionDetails) && (
                <p>{formData.otherConditionDetails}</p>
              )}
              {hasValue(formData.hypertension) && (
                <p>{formData.hypertension}</p>
              )}
              {formData.bloodPressureLastYear === 'yes' &&
                hasValue(formData.bloodPressureValue) && (
                  <p>Blodtryck: {formData.bloodPressureValue}</p>
                )}
            </SectionCard>
          )}

          {hasAny(formData.height, formData.weight, formData.bmi) && (
            <SectionCard title="BMI">
              {hasValue(formData.height) && (
                <p>Patientens längd: {formData.height} cm</p>
              )}
              {hasValue(formData.weight) && <p>Vikt: {formData.weight} kg</p>}
              {typeof formData.bmi === 'number' &&
                !Number.isNaN(formData.bmi) && (
                  <p>BMI: {Math.round(formData.bmi)}</p>
                )}
            </SectionCard>
          )}

          {hasAny(formData.biologicalSex) && (
            <SectionCard title="Övrigt">
              <p>Biologiskt kön: {formData.biologicalSex}</p>
            </SectionCard>
          )}

          {hasAny(
            formData.pregnant,
            formatPregnancyWeek(formData.pregnancyWeek),
            formData.pregnancyPlan,
            formData.contraceptiveType,
            formData.breastfeeding
          ) && (
            <SectionCard title="Graviditet">
              {hasValue(formData.pregnant) && <p>{formData.pregnant}</p>}
              {typeof formData.pregnancyWeek === 'number' && (
                <p>{formatPregnancyWeek(formData.pregnancyWeek)}</p>
              )}
              {hasValue(formData.pregnancyPlan) && (
                <p>{formData.pregnancyPlan}</p>
              )}
              {hasValue(formData.contraceptiveType) &&
                formData.contraceptiveUse !== null && (
                  <p>Preventivmedel: {formData.contraceptiveType}</p>
                )}
              {hasValue(formData.breastfeeding) && (
                <p>{formatBreastfeeding(formData.breastfeeding)}</p>
              )}
            </SectionCard>
          )}

          {hasValue(formData.additionalInfo) && (
            <SectionCard title="Övrig information">
              <p>{formData.additionalInfo}</p>
            </SectionCard>
          )}
        </JournalCopyPanel>
      )}
    </Box>
  );
};

export default NewVisitJournal;
